import { Input, Modal, Pagination, message } from 'antd'
import type { GetProps } from 'antd'
import { useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import api from '../../app/api'
import { BASE_URL } from '../../app/config'
import { Container } from '../../components/HOC/container'
import { BackButton } from '../../components/BackButton'
import { slugify } from '../../shared/translit'
import { isRepeatService } from '../../shared/serviceFilters'
import { UserContext, UserContextType } from '../../shared/contexts'
import { setToken } from '../../shared/app'
import { useTranslation } from '../../shared/hooks/useTranslation'
import {
  getCachedDoctorSlots,
  getStaleDoctorSlots,
  setCachedDoctorSlots,
  type DoctorDaySlotsRecord,
} from '../../shared/slotsCache'
import {
  formatPrice,
  formatSlotDate,
  formatWeekday,
  getMondayDateString,
  parseSlotDate,
} from '../../shared/dateFormat'
import style from './Doctor.module.scss'

const { Search } = Input
type SearchProps = GetProps<typeof Input.Search>

const DOCTORS_PER_PAGE = 10

type Review = {
  author: string
  date: string
  text: string
  rating: number
}

type DoctorItem = {
  doktor_id: string
  // Публичный ID для ссылки на запись (specialists.site_id). Может отличаться от
  // doktor_id (DR-кода для МИС). Пусто → ссылка строится по doktor_id.
  public_id?: string
  doktor_name: string
  podrazdelenie_name: string
  usluga_name?: string
  price: number
  category?: string
  photo?: string
  degree?: string
  bio?: string
  rating?: number
  reviews?: Review[]
  reviews_link?: string
  service_variants?: Array<{
    service_name: string
    price: number
  }>
}

type ExternalSlot = {
  saat?: string
  tarih?: string
  randevuId?: string | number
}

type ExternalSlotsDay = {
  rndv?: ExternalSlot[]
}

type ExternalSlotsResponse = {
  rndvList?: ExternalSlotsDay[]
}

type DoctorDaySlots = DoctorDaySlotsRecord

function resolvePhotoUrl(photo?: string): string | undefined {
  if (!photo) return undefined
  if (photo.startsWith('http://') || photo.startsWith('https://')) return photo
  if (photo.startsWith('/')) return `${BASE_URL}${photo}`
  return `${BASE_URL}/${photo}`
}

function getDayLabel(
  dateStr: string,
  language: string,
  todayWord: string,
  tomorrowWord: string,
  format: 'short' | 'long' = 'short',
): string {
  const date = parseSlotDate(dateStr)
  if (!date) return ''

  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)
  const checkDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())

  if (checkDate.getTime() === today.getTime()) {
    return todayWord
  }
  if (checkDate.getTime() === tomorrow.getTime()) {
    return tomorrowWord
  }

  return formatWeekday(checkDate, language, format)
}

function DoctorAvatar({
  name,
  photo,
  reviewsLink,
}: {
  name: string
  photo?: string
  reviewsLink?: string
}) {
  const resolvedPhoto = resolvePhotoUrl(photo)
  if (resolvedPhoto) {
    if (reviewsLink) {
      return (
        <a
          href={reviewsLink}
          target="_blank"
          rel="noopener noreferrer"
          className={style.avatarLink}
        >
          <img
            src={resolvedPhoto}
            alt={name}
            className={`${style.avatar} ${style.avatarClickable}`}
          />
        </a>
      )
    }
    return <img src={resolvedPhoto} alt={name} className={style.avatar} />
  }
  return (
    <div className={style.avatarPlaceholder}>
      <svg className={style.avatarIcon} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="32" cy="22" r="10" stroke="currentColor" strokeWidth="2.2" fill="none" />
        <path d="M12 52c0-11.046 8.954-20 20-20s20 8.954 20 20" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" fill="none" />
        <circle cx="32" cy="46" r="3" fill="currentColor" />
        <line x1="32" y1="43" x2="32" y2="38" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    </div>
  )
}

function StarRating({ rating }: { rating: number }) {
  return (
    <span className={style.stars}>
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          className={star <= rating ? style.starFilled : style.starEmpty}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </span>
  )
}

type DropdownProps = {
  isOpen: boolean
  onClose: () => void
  trigger: ReactNode
  children: ReactNode
  menuClassName?: string
}

function Dropdown({ isOpen, onClose, trigger, children, menuClassName }: DropdownProps) {
  const triggerRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [coords, setCoords] = useState<{
    openUp: boolean
    top: number
    bottom: number
    left: number
    width: number
    maxHeight: number
  } | null>(null)

  useEffect(() => {
    if (!isOpen) return
    const update = () => {
      const rect = triggerRef.current?.getBoundingClientRect()
      if (!rect) return
      const margin = 8
      const desired = 280
      const spaceBelow = window.innerHeight - rect.bottom - margin
      const spaceAbove = rect.top - margin
      // Открываем вверх только если снизу мало места, а сверху больше.
      const openUp = spaceBelow < desired && spaceAbove > spaceBelow
      // Высота ограничена доступным местом, чтобы меню не вылезало за экран.
      const maxHeight = Math.max(120, Math.min(desired, openUp ? spaceAbove : spaceBelow))
      setCoords({
        openUp,
        // Открытие вниз — якорь по верхней грани; вверх — по нижней
        // (меню растёт вверх по своей реальной высоте, без зазора).
        top: rect.bottom + 4,
        bottom: window.innerHeight - rect.top + 4,
        left: rect.left,
        width: rect.width,
        maxHeight,
      })
    }
    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    const handler = (e: MouseEvent) => {
      if (
        !triggerRef.current?.contains(e.target as Node) &&
        !menuRef.current?.contains(e.target as Node)
      ) {
        onClose()
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', handler)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', handler)
      document.removeEventListener('keydown', onKey)
    }
  }, [isOpen, onClose])

  return (
    <>
      <div ref={triggerRef} className={style.selectWrap}>
        {trigger}
      </div>
      {isOpen && coords &&
        createPortal(
          <div
            ref={menuRef}
            className={`${style.selectMenu} ${menuClassName || ''}`}
            style={
              coords.openUp
                ? { bottom: coords.bottom, left: coords.left, width: coords.width, maxHeight: coords.maxHeight }
                : { top: coords.top, left: coords.left, width: coords.width, maxHeight: coords.maxHeight }
            }
          >
            {children}
          </div>,
          document.body,
        )}
    </>
  )
}

export function Doctor() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t, language } = useTranslation()
  // В URL теперь короткий id услуги (например /service/123). Название и
  // варианты услуги передаются через sessionStorage, чтобы не светить токен
  // и длинные названия в адресной строке.
  const { name: serviceParam, doctorId } = useParams<{ name?: string; doctorId?: string }>()
  // Режим персональной ссылки врача (/doctor/id/<doktorId>): показываем список
  // врачей, но с единственным врачом; поиск/фильтры/пагинация не нужны.
  const isSingleDoctor = Boolean(doctorId)
  const serviceMeta = useMemo<{
    name: string
    variants: string[]
    uslugaIds?: string[]
    podrazdelenieIds?: string[]
  } | null>(() => {
    if (!serviceParam) return null
    try {
      return JSON.parse(sessionStorage.getItem(`svc:${serviceParam}`) || 'null')
    } catch {
      return null
    }
  }, [serviceParam])
  const [doctors, setDoctors] = useState<DoctorItem[]>([])
  const [allDoctors, setAllDoctors] = useState<DoctorItem[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'adult' | 'child'>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [isFilteringBySlots, setIsFilteringBySlots] = useState(false)
  const [slotsByDoctor, setSlotsByDoctor] = useState<Record<string, DoctorDaySlots[]>>({})
  const [loadingSlotsByDoctor, setLoadingSlotsByDoctor] = useState<Record<string, boolean>>({})
  const [selectedDayByDoctor, setSelectedDayByDoctor] = useState<Record<string, string>>({})
  // Выбранный слот времени по врачу. Выбор слота больше не переводит сразу на форму —
  // переход происходит только по кнопке «Записаться».
  const [selectedSlotByDoctor, setSelectedSlotByDoctor] = useState<
    Record<string, { value: string; label: string; time: string }>
  >({})
  const [openDropdown, setOpenDropdown] = useState<{
    doctorId: string
    kind: 'date' | 'time'
  } | null>(null)
  const [expandedBioByDoctor, setExpandedBioByDoctor] = useState<Record<string, boolean>>({})
  const { user, setUser } = useContext<UserContextType>(UserContext)
  const [reviewsModal, setReviewsModal] = useState<{
    open: boolean
    doctor: DoctorItem | null
  }>({ open: false, doctor: null })

  const goToNextStep = (doctor: DoctorItem) => {
    const selectedServiceName =
      doctor.service_variants?.slice().sort((a, b) => a.price - b.price)[0]?.service_name ||
      (serviceMeta?.name || serviceParam)

    setUser({
      ...user,
      serviceId: selectedServiceName,
      doctorId: doctor.doktor_id,
      doctorName: doctor.doktor_name,
      doctorType: doctor.podrazdelenie_name,
      doctorPrice: doctor.price,
    } as UserContextType['user'])
    // ФИО врача латиницей в адресной строке: /form/ivanov-ivan.
    const doctorSlug = slugify(doctor.doktor_name || '')
    navigate(doctorSlug ? `/form/${doctorSlug}` : '/form')
  }

  const goToNextStepWithSlot = (
    doctor: DoctorItem,
    slot: { value: string; label: string },
  ) => {
    const selectedServiceName =
      doctor.service_variants?.slice().sort((a, b) => a.price - b.price)[0]?.service_name ||
      (serviceMeta?.name || serviceParam)

    setUser({
      ...user,
      serviceId: selectedServiceName,
      doctorId: doctor.doktor_id,
      doctorName: doctor.doktor_name,
      doctorType: doctor.podrazdelenie_name,
      doctorPrice: doctor.price,
      date: slot.value,
      selectedDateTime: slot.label,
    } as UserContextType['user'])
    // ФИО врача латиницей в адресной строке: /form/ivanov-ivan.
    const doctorSlug = slugify(doctor.doktor_name || '')
    navigate(doctorSlug ? `/form/${doctorSlug}` : '/form')
  }

  // Копирует персональную ссылку врача (/doctor/id/<id>) в буфер обмена.
  // id — публичный (public_id / site_id) при наличии, иначе doktor_id.
  const copyDoctorLink = async (docId: string) => {
    const url = `${window.location.origin}/doctor/id/${docId}`
    try {
      await navigator.clipboard.writeText(url)
      message.success(t.doctorLinkCopied)
    } catch {
      // clipboard может быть недоступен (не-https/ограничения) — показываем ссылку.
      message.info(url)
    }
  }

  const onSearch: SearchProps['onSearch'] = (value) => {
    setSearchQuery(value)
  }

  const applyDoctorsFilters = (
    doctorsToFilter: DoctorItem[],
    categoryFilter: 'all' | 'adult' | 'child',
    query: string,
    slots: Record<string, DoctorDaySlots[]>,
  ) => {
    const normalizedQuery = query.trim().toLowerCase()
    const isAdultFilter = categoryFilter === 'adult'
    const isChildFilter = categoryFilter === 'child'

    return doctorsToFilter
      .filter((doctor) => {
        const normalizedCategory = (doctor.category || '').trim().toLowerCase()
        if (isAdultFilter && normalizedCategory !== 'взрослый') {
          return false
        }
        if (isChildFilter && normalizedCategory !== 'детский') {
          return false
        }
        if (!normalizedQuery) {
          return true
        }
        return doctor.doktor_name.toLowerCase().includes(normalizedQuery)
      })
      .sort((a, b) => {
        const aHasSlots = (slots[a.doktor_id] || []).length > 0 ? 0 : 1
        const bHasSlots = (slots[b.doktor_id] || []).length > 0 ? 0 : 1
        if (aHasSlots !== bHasSlots) return aHasSlots - bHasSlots
        return a.doktor_name.localeCompare(b.doktor_name, 'ru')
      })
  }

  useEffect(() => {
    let isCancelled = false

    const fetchDoctorSlots = async (doctorId: string, token: string): Promise<DoctorDaySlots[]> => {
      const haftas = [getMondayDateString(0), getMondayDateString(1)]
      const groups = new Map<string, DoctorDaySlots>()

      const responses = await Promise.all(
        haftas.map((hafta) =>
          api
            .get<ExternalSlotsResponse>('/external/slots', {
              params: { token, doktorId: doctorId, hafta, lang: language },
            })
            .catch((err) => {
              console.error(
                `[slots] fetch failed doctorId=${doctorId} hafta=${hafta}`,
                err?.response?.status,
                err?.response?.data || err?.message,
              )
              return null
            }),
        ),
      )

      responses.forEach((res) => {
        if (!res) return
        res.data?.rndvList?.forEach((day) => {
          day.rndv?.forEach((slot) => {
            if (!slot?.saat || !slot?.tarih) return
            const existing = groups.get(slot.tarih)
            if (existing) {
              if (!existing.slots.some((s) => s.time === slot.saat)) {
                existing.slots.push({
                  value: String(slot.randevuId || ''),
                  time: slot.saat,
                  label: `${formatSlotDate(slot.tarih, language)}, ${slot.saat}`,
                })
              }
              return
            }
            groups.set(slot.tarih, {
              dateKey: slot.tarih,
              dayLabel: getDayLabel(slot.tarih, language, t.doctorTodayWord, t.doctorTomorrowWord),
              fullDayLabel: getDayLabel(slot.tarih, language, t.doctorTodayWord, t.doctorTomorrowWord, 'long'),
              dateLabel: formatSlotDate(slot.tarih, language),
              slots: [
                {
                  value: String(slot.randevuId || ''),
                  time: slot.saat,
                  label: `${formatSlotDate(slot.tarih, language)}, ${slot.saat}`,
                },
              ],
            })
          })
        })
      })

      return Array.from(groups.values()).sort((a, b) => {
        const da = parseSlotDate(a.dateKey)
        const db = parseSlotDate(b.dateKey)
        if (!da || !db) return 0
        return da.getTime() - db.getTime()
      })
    }

    const loadDoctorsAndSlots = async () => {
      if (!serviceParam && !doctorId) return

      if (!isCancelled) {
        setIsFilteringBySlots(true)
        setDoctors([])
        setAllDoctors([])
      }

      const params = new URLSearchParams(location.search)
      // Бэк-совместимость: старые ссылки могли нести токен в query.
      const urlToken = params.get('token')?.trim()
      if (urlToken) {
        setToken(urlToken)
        setUser((prev) => ({ ...(prev || {}), token: urlToken }))
      }
      const token = localStorage.getItem('token')
      try {
        const doctorsById = new Map<string, DoctorItem>()

        // Плоский список строк-врачей вместе с названием услуги, к которой
        // относится строка (для service_variants и цены).
        const doctorRows: Array<{ doctor: DoctorItem; serviceName: string }> = []

        if (isSingleDoctor) {
          // Персональная ссылка врача: один врач со всеми его услугами.
          const res = await api.get('/services/doctors', {
            params: { doktorId: doctorId, token, lang: language },
          })
          const doctorsList = Array.isArray(res.data) ? (res.data as DoctorItem[]) : []
          // Повторные приёмы убираем — по личной ссылке записывают на первичный;
          // если у врача только повторные, оставляем как есть.
          const primary = doctorsList.filter((d) => !isRepeatService(d.usluga_name))
          const effective = primary.length > 0 ? primary : doctorsList
          effective.forEach((doctor) => {
            doctorRows.push({ doctor, serviceName: doctor.usluga_name || '' })
          })
        } else {
          // Языконезависимая идентификация: список usluga_id из sessionStorage.
          // По ним бэк ищет врачей независимо от языка и написания названия.
          const uslugaIds = (serviceMeta?.uslugaIds || []).filter(Boolean)

          // Fallback по названию — для старых ссылок/сессий без uslugaIds.
          const urlVariants = params.getAll('svc').filter(Boolean)
          const serviceVariantsFromQuery = (
            serviceMeta?.variants?.length ? serviceMeta.variants : urlVariants
          ).filter(Boolean)
          const fallbackName = serviceMeta?.name || decodeURIComponent(serviceParam || '')
          const serviceNames =
            serviceVariantsFromQuery.length > 0
              ? serviceVariantsFromQuery
              : fallbackName
              ? [fallbackName]
              : []

          if (uslugaIds.length > 0) {
            const podrazdelenieIds = (serviceMeta?.podrazdelenieIds || []).filter(Boolean)
            const res = await api.get('/services/doctors', {
              params: {
                uslugaIds: uslugaIds.join(','),
                ...(podrazdelenieIds.length > 0
                  ? { podrazdelenieIds: podrazdelenieIds.join(',') }
                  : {}),
                token,
                lang: language,
              },
            })
            const doctorsList = Array.isArray(res.data) ? (res.data as DoctorItem[]) : []
            doctorsList.forEach((doctor) => {
              doctorRows.push({
                doctor,
                // Локализованное название услуги приходит с бэка — не зависим от
                // языка, в котором услугу выбирали изначально.
                serviceName: doctor.usluga_name || fallbackName,
              })
            })
          } else {
            const podrazdelenieIds = (serviceMeta?.podrazdelenieIds || []).filter(Boolean)
            const doctorsResponses = await Promise.all(
              serviceNames.map((serviceNameVariant) =>
                api.get('/services/doctors', {
                  params: {
                    name: serviceNameVariant,
                    ...(podrazdelenieIds.length > 0
                      ? { podrazdelenieIds: podrazdelenieIds.join(',') }
                      : {}),
                    token,
                    lang: language,
                  },
                }),
              ),
            )
            doctorsResponses.forEach((res, index) => {
              const serviceNameVariant = serviceNames[index]
              // Бэк может вернуть null (Go: nil slice → JSON null), когда услуга
              // не найдена. Cross-language OR на бэке защищает от смены языка.
              const doctorsList = Array.isArray(res.data) ? (res.data as DoctorItem[]) : []
              doctorsList.forEach((doctor) => {
                doctorRows.push({ doctor, serviceName: serviceNameVariant })
              })
            })
          }
        }

        doctorRows.forEach(({ doctor, serviceName }) => {
          const existing = doctorsById.get(doctor.doktor_id)
          if (!existing) {
            doctorsById.set(doctor.doktor_id, {
              ...doctor,
              service_variants: [
                {
                  service_name: serviceName,
                  price: doctor.price,
                },
              ],
            })
            return
          }

          const variants = existing.service_variants || []
          if (!variants.some((v) => v.service_name === serviceName)) {
            variants.push({
              service_name: serviceName,
              price: doctor.price,
            })
          }
          existing.service_variants = variants
          existing.price = Math.min(existing.price, ...variants.map((v) => v.price))
        })

        const sorted = Array.from(doctorsById.values()).sort((a: DoctorItem, b: DoctorItem) =>
          a.doktor_name.localeCompare(b.doktor_name, 'ru'),
        )

        if (isCancelled) return

        // Показываем врачей сразу, а слоты догружаем фоном.
        setAllDoctors(sorted)

        if (!token) {
          setIsFilteringBySlots(false)
          return
        }

        const ids: string[] = Array.from(
          new Set(
            sorted
              .map((doctor: DoctorItem) => doctor.doktor_id)
              .filter((id: string) => Boolean(id)),
          ),
        )

        const initialLoading: Record<string, boolean> = {}
        ids.forEach((id: string) => {
          initialLoading[id] = true
        })
        const resolvedSlots: Record<string, DoctorDaySlots[]> = {}
        const idsToFetch: string[] = []

        // stale-while-revalidate: если есть протухший кэш — показываем его
        // сразу (loading=false), но всё равно перезапрашиваем в фоне.
        // Без этого после возврата со страницы регистрации (где TTL мог
        // истечь) слоты схлопывались бы в пустой список.
        ids.forEach((id) => {
          const fresh = getCachedDoctorSlots(language, id, token)
          if (fresh) {
            resolvedSlots[id] = fresh
            initialLoading[id] = false
            return
          }
          const stale = getStaleDoctorSlots(language, id, token)
          if (stale) {
            resolvedSlots[id] = stale
            initialLoading[id] = false
          }
          idsToFetch.push(id)
        })

        setLoadingSlotsByDoctor(initialLoading)
        setSlotsByDoctor(resolvedSlots)
        setSelectedDayByDoctor({})
        setOpenDropdown(null)
        setIsFilteringBySlots(false)

        if (idsToFetch.length > 0) {
          const queue = [...idsToFetch]
          const concurrency = Math.min(12, queue.length)

          const worker = async () => {
            while (queue.length > 0) {
              const slotDoctorId = queue.shift()
              if (!slotDoctorId) return

              const slots = await fetchDoctorSlots(slotDoctorId, token)
              if (isCancelled) return

              // Защита от моргания: если фоновое обновление вернуло пусто,
              // а у нас уже отрисован stale-кэш — оставляем как было.
              const hadShownSlots = (resolvedSlots[slotDoctorId] || []).length > 0
              if (slots.length === 0 && hadShownSlots) {
                setLoadingSlotsByDoctor((prev) => ({ ...prev, [slotDoctorId]: false }))
                continue
              }

              resolvedSlots[slotDoctorId] = slots
              if (slots.length > 0) {
                setCachedDoctorSlots(language, slotDoctorId, token, slots)
              }
              setSlotsByDoctor((prev) => ({ ...prev, [slotDoctorId]: slots }))
              setLoadingSlotsByDoctor((prev) => ({ ...prev, [slotDoctorId]: false }))
            }
          }

          await Promise.all(Array.from({ length: concurrency }, () => worker()))
        }

      } catch {
        if (isCancelled) return
        setDoctors([])
        setAllDoctors([])
        setIsFilteringBySlots(false)
      }
    }

    loadDoctorsAndSlots()
    return () => {
      isCancelled = true
    }
  }, [serviceParam, serviceMeta, language, location.search, doctorId, isSingleDoctor])

  useEffect(() => {
    setDoctors(applyDoctorsFilters(allDoctors, selectedCategory, searchQuery, slotsByDoctor))
  }, [allDoctors, searchQuery, selectedCategory, slotsByDoctor])

  // Сбрасываем страницу при изменении фильтров/поиска.
  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery, selectedCategory, allDoctors])

  const reviewDoctor = reviewsModal.doctor
  const reviews = reviewDoctor?.reviews || []

  const slotTitle = t.doctorSlotTitle
  const noSlotsText = t.doctorNoSlots
  const slotsCountSuffix = (n: number) => {
    if (language === 'en') return n === 1 ? 'slot' : 'slots'
    if (language === 'kk') return 'слот'
    // Русские плюральные правила для слов
    const mod10 = n % 10
    const mod100 = n % 100
    if (mod10 === 1 && mod100 !== 11) return 'слот'
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'слота'
    return 'слотов'
  }
  const chooseTimeText = t.doctorChooseTime
  const capitalize = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s)
  const bioMoreText = t.doctorBioMore
  const BIO_MAX_LENGTH = 230
  const filteringText = t.doctorFilteringText
  const searchingDoctorsText = t.doctorSearchingText
  const categoryAllText = t.doctorCategoryAll
  const categoryAdultText = t.doctorCategoryAdult
  const categoryChildText = t.doctorCategoryChild

  const totalDoctors = (doctors ?? []).length
  const pagedDoctors = (doctors ?? []).slice(
    (currentPage - 1) * DOCTORS_PER_PAGE,
    currentPage * DOCTORS_PER_PAGE,
  )

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <Container>
      <div className={style.menu}>
        <div className={style.menuLeft}>
          <BackButton className={style.back} />
          {!isSingleDoctor && (
            <Search
              placeholder={t.doctorSearchPlaceholder}
              onSearch={onSearch}
              onChange={(e) => setSearchQuery(e.target.value)}
              value={searchQuery}
              className={style.search}
            />
          )}
        </div>
        {!isSingleDoctor && (
        <div className={style.categoryToggle}>
          <button
            type="button"
            className={`${style.toggleItem} ${selectedCategory === 'all' ? style.toggleItemActive : ''}`}
            onClick={() => setSelectedCategory('all')}
          >
            {categoryAllText}
          </button>
          <button
            type="button"
            className={`${style.toggleItem} ${selectedCategory === 'adult' ? style.toggleItemActive : ''}`}
            onClick={() => setSelectedCategory('adult')}
          >
            {categoryAdultText}
          </button>
          <button
            type="button"
            className={`${style.toggleItem} ${selectedCategory === 'child' ? style.toggleItemActive : ''}`}
            onClick={() => setSelectedCategory('child')}
          >
            {categoryChildText}
          </button>
        </div>
        )}
      </div>

      {isFilteringBySlots && (
        <div className={style.loaderWrap}>
          <div className={style.loaderRing} />
          <p className={style.loaderText}>{searchingDoctorsText}</p>
          <p className={style.stateMessage}>{filteringText}</p>
        </div>
      )}

      <div className={style.list}>
        {pagedDoctors.map((item) => {
          const doctorSlots = slotsByDoctor[item.doktor_id] || []
          const selectedDayKey =
            selectedDayByDoctor[item.doktor_id] ?? doctorSlots[0]?.dateKey
          const selectedDay =
            doctorSlots.find((day) => day.dateKey === selectedDayKey) || doctorSlots[0]
          const selectedSlot = selectedSlotByDoctor[item.doktor_id]

          return (
            <div key={item.doktor_id} className={style.card}>
              {/* Left: photo */}
              <div className={style.colPhoto}>
                <DoctorAvatar
                  name={item.doktor_name}
                  photo={item.photo}
                  reviewsLink={item.reviews_link}
                />
                {item.rating != null && (
                  <div className={style.ratingBadge}>
                    <svg className={style.ratingStarIcon} viewBox="0 0 20 20" fill="currentColor">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    <span>{item.rating.toFixed(1)}</span>
                  </div>
                )}
              </div>

              {/* Center: info */}
              <div className={style.infoBlock}>
                <h3 className={style.name}>
                  {item.reviews_link ? (
                    <a
                      href={item.reviews_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={style.nameLink}
                    >
                      {item.doktor_name}
                    </a>
                  ) : (
                    item.doktor_name
                  )}
                  <button
                    type="button"
                    className={style.copyLinkBtn}
                    onClick={() => copyDoctorLink(item.public_id || item.doktor_id)}
                    title={t.doctorCopyLink}
                    aria-label={t.doctorCopyLink}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <circle cx="18" cy="5" r="3" />
                      <circle cx="6" cy="12" r="3" />
                      <circle cx="18" cy="19" r="3" />
                      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                    </svg>
                  </button>
                </h3>
                <span className={style.specialty}>{item.degree || item.podrazdelenie_name}</span>
                <span className={style.clinic}>{item.podrazdelenie_name}</span>

                <span className={style.priceInline}>
                  {t.experience}: {formatPrice(item.price, language)} {t.tenge}
                </span>

                {item.bio && (
                  <div className={style.bioBlock}>
                    <span className={style.bioLabel}>{t.about}:</span>
                    <p className={style.bio}>
                      {expandedBioByDoctor[item.doktor_id] || item.bio.length <= BIO_MAX_LENGTH
                        ? item.bio
                        : item.bio.slice(0, BIO_MAX_LENGTH)}
                      {item.bio.length > BIO_MAX_LENGTH && !expandedBioByDoctor[item.doktor_id] && (
                        <>
                          {'... '}
                          <span
                            className={style.bioToggle}
                            onClick={() =>
                              setExpandedBioByDoctor((prev) => ({
                                ...prev,
                                [item.doktor_id]: true,
                              }))
                            }
                          >
                            {bioMoreText}
                          </span>
                        </>
                      )}
                    </p>
                  </div>
                )}

                <div className={style.actionRow}>
                  {item.reviews_link && (
                    <a
                      className={style.reviewsLink}
                      href={item.reviews_link}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <span>{t.doctorReviews}</span>
                    </a>
                  )}
                  {doctorSlots.length > 0 && (
                    <button
                      className={style.bookBtn}
                      onClick={() =>
                        selectedSlot
                          ? goToNextStepWithSlot(item, {
                              value: selectedSlot.value,
                              label: selectedSlot.label,
                            })
                          : goToNextStep(item)
                      }
                      type="button"
                    >
                      {t.bookAppointment}
                    </button>
                  )}
                </div>
              </div>

              <div className={style.slotsBlock}>
                <p className={style.slotsTitle}>{slotTitle}</p>
                {loadingSlotsByDoctor[item.doktor_id] ? (
                  <div className={style.slotsLoadingBlock}>
                    <div className={style.slotsLoadingRing} />
                  </div>
                ) : doctorSlots.length > 0 && selectedDay ? (
                  <div className={style.selectStack}>
                    {(() => {
                      const dateOpen =
                        openDropdown?.doctorId === item.doktor_id &&
                        openDropdown.kind === 'date'
                      const timeOpen =
                        openDropdown?.doctorId === item.doktor_id &&
                        openDropdown.kind === 'time'
                      return (
                        <>
                          <Dropdown
                            isOpen={dateOpen}
                            onClose={() => setOpenDropdown(null)}
                            trigger={
                              <button
                                type="button"
                                className={`${style.selectTrigger} ${dateOpen ? style.selectTriggerOpen : ''}`}
                                onClick={() =>
                                  setOpenDropdown(
                                    dateOpen
                                      ? null
                                      : { doctorId: item.doktor_id, kind: 'date' },
                                  )
                                }
                              >
                                <svg
                                  className={style.selectIcon}
                                  viewBox="0 0 20 20"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="1.6"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <rect x="3" y="4.5" width="14" height="13" rx="2" />
                                  <line x1="3" y1="8.5" x2="17" y2="8.5" />
                                  <line x1="7" y1="3" x2="7" y2="6" />
                                  <line x1="13" y1="3" x2="13" y2="6" />
                                </svg>
                                <span className={style.selectLabel}>
                                  {capitalize(selectedDay.fullDayLabel)}, {selectedDay.dateLabel}
                                </span>
                                <span className={style.selectBadge}>
                                  {selectedDay.slots.length}
                                </span>
                                <svg
                                  className={`${style.selectChevron} ${dateOpen ? style.selectChevronOpen : ''}`}
                                  viewBox="0 0 20 20"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <polyline points="6 8 10 12 14 8" />
                                </svg>
                              </button>
                            }
                          >
                            {doctorSlots.map((day) => {
                              const isActive = day.dateKey === selectedDay.dateKey
                              return (
                                <button
                                  key={day.dateKey}
                                  type="button"
                                  className={`${style.dateOption} ${isActive ? style.dateOptionActive : ''}`}
                                  onClick={() => {
                                    setSelectedDayByDoctor((prev) => ({
                                      ...prev,
                                      [item.doktor_id]: day.dateKey,
                                    }))
                                    // Сброс выбранного времени при смене даты.
                                    setSelectedSlotByDoctor((prev) => {
                                      const next = { ...prev }
                                      delete next[item.doktor_id]
                                      return next
                                    })
                                    setOpenDropdown({
                                      doctorId: item.doktor_id,
                                      kind: 'time',
                                    })
                                  }}
                                >
                                  <span className={style.dateOptionMain}>
                                    <span className={style.dateOptionDay}>
                                      {capitalize(day.fullDayLabel)}
                                    </span>
                                    <span className={style.dateOptionDate}>{day.dateLabel}</span>
                                  </span>
                                  <span className={style.dateOptionCount}>
                                    {day.slots.length} {slotsCountSuffix(day.slots.length)}
                                  </span>
                                </button>
                              )
                            })}
                          </Dropdown>

                          <Dropdown
                            isOpen={timeOpen}
                            onClose={() => setOpenDropdown(null)}
                            menuClassName={style.timeMenu}
                            trigger={
                              <button
                                type="button"
                                className={`${style.selectTrigger} ${timeOpen ? style.selectTriggerOpen : ''}`}
                                onClick={() =>
                                  setOpenDropdown(
                                    timeOpen
                                      ? null
                                      : { doctorId: item.doktor_id, kind: 'time' },
                                  )
                                }
                              >
                                <svg
                                  className={style.selectIcon}
                                  viewBox="0 0 20 20"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="1.6"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <circle cx="10" cy="10" r="7" />
                                  <polyline points="10 6 10 10 13 12" />
                                </svg>
                                <span className={style.selectLabel}>
                                  {selectedSlot ? selectedSlot.time : chooseTimeText}
                                </span>
                                <svg
                                  className={`${style.selectChevron} ${timeOpen ? style.selectChevronOpen : ''}`}
                                  viewBox="0 0 20 20"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <polyline points="6 8 10 12 14 8" />
                                </svg>
                              </button>
                            }
                          >
                            <div className={style.timeMenuGrid}>
                              {selectedDay.slots.map((slot) => (
                                <button
                                  key={`${selectedDay.dateKey}-${slot.value}-${slot.time}`}
                                  type="button"
                                  className={`${style.timeChipButton} ${
                                    selectedSlot?.value === slot.value &&
                                    selectedSlot?.time === slot.time
                                      ? style.timeChipButtonActive
                                      : ''
                                  }`}
                                  onClick={() => {
                                    setOpenDropdown(null)
                                    setSelectedSlotByDoctor((prev) => ({
                                      ...prev,
                                      [item.doktor_id]: {
                                        value: slot.value,
                                        label: slot.label,
                                        time: slot.time,
                                      },
                                    }))
                                  }}
                                >
                                  {slot.time}
                                </button>
                              ))}
                            </div>
                          </Dropdown>
                        </>
                      )
                    })()}
                  </div>
                ) : (
                  <div className={style.noSlotsBlock}>
                    <svg className={style.noSlotsIcon} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="6" y="10" width="36" height="32" rx="4" stroke="currentColor" strokeWidth="2" />
                      <line x1="6" y1="20" x2="42" y2="20" stroke="currentColor" strokeWidth="2" />
                      <line x1="16" y1="6" x2="16" y2="14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      <line x1="32" y1="6" x2="32" y2="14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      <line x1="18" y1="30" x2="30" y2="30" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      <line x1="24" y1="26" x2="24" y2="34" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    <span>{noSlotsText}</span>
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {totalDoctors > DOCTORS_PER_PAGE && (
        <div className={style.pagination}>
          <Pagination
            current={currentPage}
            pageSize={DOCTORS_PER_PAGE}
            total={totalDoctors}
            onChange={handlePageChange}
            showSizeChanger={false}
          />
        </div>
      )}

      <Modal
        open={reviewsModal.open}
        onCancel={() => setReviewsModal({ open: false, doctor: null })}
        footer={null}
        title={reviewDoctor ? `${t.reviews} — ${reviewDoctor.doktor_name}` : t.reviews}
      >
        {reviews.length === 0 ? (
          <div className={style.noReviews}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              className={style.noReviewsIcon}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8.625 9.75a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 0 1 .778-.332 48.294 48.294 0 0 0 5.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z"
              />
            </svg>
            <p>{t.noReviews}</p>
          </div>
        ) : (
          <div className={style.reviewsList}>
            {reviews.map((review, idx) => (
              <div key={idx} className={style.reviewItem}>
                <div className={style.reviewHeader}>
                  <span className={style.reviewAuthor}>{review.author}</span>
                  <StarRating rating={review.rating} />
                  <span className={style.reviewDate}>{review.date}</span>
                </div>
                <p className={style.reviewText}>{review.text}</p>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </Container>
  )
}
